import { useState } from 'react'
import { Link } from 'react-router-dom'

import type { PatchCatalog as Catalog, PatchCatalogEntry } from '../../api/client'
import { t } from '../../i18n/lingua'
import { BottoniEsporta } from '../BottoniEsporta'
import { DataTable } from '../DataTable'
import { Card, EmptyState, formatDay } from '../common'
import { KevChip, PriorityChip, SeverityChip, chipClass, patchKindLabel, platformLabel, priorityLabel, severityLabel } from './labels'
import type { WizardPrefill } from './NewJobWizard'

export type PatchFilter = 'all' | 'urgent' | 'overdue' | 'exploited'

/**
 * One row per missing patch, by priority: what to install first. Tick patches
 * to install them where they are missing.
 */
export function PatchCatalog({ data, initialFilter, canWrite, onNewJob }: {
  data: Catalog
  initialFilter?: PatchFilter
  canWrite: boolean
  onNewJob: (prefill?: WizardPrefill) => void
}) {
  const [filter, setFilter] = useState<PatchFilter>(initialFilter ?? 'all')
  const [kind, setKind] = useState('')
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const match = (p: PatchCatalogEntry, f: PatchFilter) =>
    f === 'urgent' ? p.priority_label === 'urgent'
      : f === 'overdue' ? p.overdue
        : f === 'exploited' ? p.exploited : true
  const rows = data.patches.filter((p) => match(p, filter) && (!kind || p.kind === kind))
  const chosen = data.patches.filter((p) => selected.has(p.key))
  const hosts = [...new Set(chosen.flatMap((p) => p.hosts.map((h) => h.ci_id)))]

  return (
    <Card title={t('Patch mancanti')}
      actions={<BottoniEsporta path={(fmt) => `/patching/export?what=patches&fmt=${fmt}`} nome="censo-patch-mancanti" disabled={!data.patches.length} />}>
      <p className="field__hint" style={{ marginTop: 0 }}>
        {t('Una riga per patch, con le macchine a cui manca. La priorità unisce gravità, sfruttamento attivo (CISA KEV), tempi superati e numero di macchine. Le vulnerabilità di applicazioni e librerie restano in SBOM: si chiudono aggiornando l’applicazione.')}
      </p>
      <div className="patch-toolbar">
        <div className="segmented" role="group" aria-label={t('Filtro')}>
          {([
            ['all', t('Tutte')], ['urgent', t('Urgenti')], ['overdue', t('Fuori SLA')], ['exploited', t('Sfruttate')],
          ] as Array<[PatchFilter, string]>).map(([k, label]) => (
            <button key={k} type="button" className={filter === k ? 'segmented__on' : ''} onClick={() => setFilter(k)}>
              {label} ({data.patches.filter((p) => match(p, k)).length})
            </button>
          ))}
        </div>
        <select value={kind} onChange={(e) => setKind(e.target.value)} aria-label={t('Tipo')}>
          <option value="">{t('Tutti i tipi')}</option>
          <option value="kb">{t('KB Windows')}</option>
          <option value="package">{t('Pacchetti Linux')}</option>
          <option value="software">{t('Software installato')}</option>
        </select>
      </div>
      {canWrite && selected.size > 0 && (
        <div className="patch-selbar">
          <strong>{t('{n} patch scelte, su {m} macchine', { n: selected.size, m: hosts.length })}</strong>
          <button onClick={() => onNewJob({ kind: 'selected', patchKeys: [...selected], ciIds: hosts })}>
            {t('Installa le patch scelte')}
          </button>
          <button className="secondary" onClick={() => setSelected(new Set())}>{t('Deseleziona')}</button>
        </div>
      )}
      {rows.length === 0 ? (
        <EmptyState title={data.patches.length ? t('Nessuna patch con questo filtro.') : t('Nessuna patch mancante.')} />
      ) : (
        <DataTable
          rows={rows}
          rowKey={(r) => r.key}
          conteggio={{ nome: t('patch') }}
          paginazione={{ predefinita: 25 }}
          initialSort={{ key: 'priority', dir: 'desc' }}
          selezione={canWrite ? {
            chiavi: selected,
            onCambia: (s) => setSelected(new Set([...s].filter((k) => data.patches.find((p) => p.key === k)?.installable))),
            id: (r) => r.key,
          } : undefined}
          expand={(r) => <PatchDetail p={r} />}
          columns={[
            {
              key: 'priority', header: t('Priorità'), value: (r) => r.priority,
              render: (r) => <PriorityChip priority={r.priority_label} />,
              filter: false,
            },
            {
              key: 'name', header: t('Patch'), value: (r) => `${r.name} ${r.title}`,
              render: (r) => (
                <>
                  <span className="mono">{r.name}</span>
                  <div className="text-muted patch-sub">
                    {patchKindLabel(r.kind)}
                    {r.title && r.title !== r.name ? ` · ${r.title}` : ''}
                  </div>
                </>
              ),
            },
            {
              key: 'platform', header: t('Sistema'), value: (r) => r.platforms.map(platformLabel).join(', '),
              filter: 'select',
            },
            {
              key: 'severity', header: t('Gravità'), value: (r) => severityLabel(r.severity),
              render: (r) => <SeverityChip severity={r.severity} />, filter: 'select',
            },
            {
              key: 'exploited', header: t('Sfruttata'), value: (r) => (r.exploited ? t('sì') : ''),
              render: (r) => (r.exploited ? <KevChip /> : ''), filter: 'select',
            },
            { key: 'hosts', header: t('Macchine'), value: (r) => r.host_count },
            {
              key: 'age', header: t('Da'), value: (r) => r.age_days,
              render: (r) => (
                <>
                  {t('{n} giorni', { n: r.age_days })}
                  {r.overdue && <> <span className={chipClass('warn')}>{t('oltre SLA')}</span></>}
                </>
              ),
            },
            { key: 'cves', header: 'CVE', value: (r) => r.cve_count },
            {
              key: 'action', header: '', value: () => '', sort: false, filter: false,
              render: (r) => (canWrite && r.installable ? (
                <button className="link" onClick={() => onNewJob({
                  kind: 'selected', patchKeys: [r.key], ciIds: r.hosts.map((h) => h.ci_id),
                  name: t('Installa {p}', { p: r.name }),
                })}>{t('Installa')}</button>
              ) : null),
            },
          ]}
        />
      )}
    </Card>
  )
}

function PatchDetail({ p }: { p: PatchCatalogEntry }) {
  return (
    <div className="patch-machine">
      <p>
        <strong>{priorityLabel(p.priority_label)}</strong>{' · '}
        {t('gravità {s}, vista per la prima volta il {d}, tempi: {n} giorni', {
          s: severityLabel(p.severity).toLowerCase(), d: formatDay(p.first_seen), n: p.sla_days,
        })}
        {p.fixed && <> · {t('versione corretta {v}', { v: p.fixed })}</>}
      </p>
      <h4 className="patch-machine__title">{t('Macchine a cui manca ({n})', { n: p.host_count })}</h4>
      <ul className="patch-hosts">
        {p.hosts.map((h) => <li key={h.ci_id}><Link to={`/cis/${h.ci_id}`}>{h.name || h.ci_id.slice(0, 8)}</Link></li>)}
      </ul>
      {p.cves.length > 0 && (
        <>
          <h4 className="patch-machine__title">CVE</h4>
          <p className="mono patch-cves">{p.cves.join(', ')}{p.cve_count > p.cves.length ? ' …' : ''}</p>
        </>
      )}
      {p.kind === 'software' && (
        <p className="field__hint">{t('È un programma installato: su Windows l’agente lo aggiorna con winget o Chocolatey della macchina (Chrome ed Edge anche con il loro aggiornatore), su Linux dal suo repository. Senza winget né Chocolatey la macchina lo dice nel riepilogo.')}</p>
      )}
    </div>
  )
}
